/* eslint-disable react/require-default-props */
import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  fmtBRL,
  fmtBRLCompact,
  fmtInt,
  fmtMes,
  mediana,
  REGIOES,
  SearchHit,
  TimeseriesPoint,
  VolumePoint,
} from 'services/price_intelligence';

const DATA_COLOR = '#009F79';

const rotuloCurto = (v: number): string => {
  if (Math.abs(v) >= 10000) return fmtBRLCompact(v);
  if (Math.abs(v) >= 100) return `R$ ${Math.round(v).toLocaleString('pt-BR')}`;
  return fmtBRL(v);
};
const GRID_COLOR = 'rgba(33, 53, 74, 0.08)';
const TICK_COLOR = 'rgba(33, 53, 74, 0.45)';

const CardShell = styled.div`
  position: relative;
  background: #fff;
  border: 1px solid ${(props) => props.theme.$gray300};
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(33, 53, 74, 0.06);
  padding: 18px;
  height: 100%;

  .chart-title {
    margin: 0;
    font-size: 0.92rem;
    font-weight: 700;
    color: ${(props) => props.theme.$gray900};
  }

  .chart-sub {
    margin: 2px 0 12px;
    font-size: 0.78rem;
    color: ${(props) => props.theme.$gray600};
  }

  .chart-empty {
    font-size: 0.85rem;
    color: ${(props) => props.theme.$gray500};
    margin: 0;
  }

  .chart-note {
    margin: 6px 0 0;
    font-size: 0.74rem;
    color: ${(props) => props.theme.$gray500};
  }

  .region-row {
    display: flex;
    align-items: center;
    gap: 10px;

    & + .region-row {
      margin-top: 10px;
    }

    .region-name {
      width: 96px;
      flex-shrink: 0;
      font-size: 0.8rem;
      color: ${(props) => props.theme.$gray600};
    }

    .region-track {
      flex: 1;
      min-width: 0;
      height: 18px;
    }

    .region-bar {
      height: 100%;
      border-radius: 0 4px 4px 0;
      background: ${DATA_COLOR};
      opacity: 0.35;
    }

    .region-value {
      width: 150px;
      flex-shrink: 0;
      text-align: right;
      font-size: 0.8rem;
      color: ${(props) => props.theme.$gray800};
      font-variant-numeric: tabular-nums;

      small {
        margin-left: 4px;
        color: ${(props) => props.theme.$gray500};
      }
    }

    &.emph {
      .region-name {
        font-weight: 700;
        color: ${(props) => props.theme.$gray900};
      }

      .region-bar {
        opacity: 1;
      }
    }
  }

  .chart-toggle {
    display: flex;
    justify-content: flex-end;
    gap: 4px;
    margin: -4px 0 6px;

    button {
      border: 1px solid ${(props) => props.theme.$gray300};
      background: #fff;
      border-radius: 999px;
      padding: 1px 10px;
      font-size: 0.72rem;
      color: ${(props) => props.theme.$gray600};

      &.active {
        background: ${(props) => props.theme.$bg_green};
        border-color: ${(props) => props.theme.$secondary_green_light};
        color: ${(props) => props.theme.$dark_green};
        font-weight: 700;
      }
    }
  }

  .chart-tooltip {
    pointer-events: none;
    position: absolute;
    z-index: 10;
    transform: translate(-50%, -100%);
    white-space: nowrap;
    background: #fff;
    border: 1px solid ${(props) => props.theme.$gray300};
    border-radius: 8px;
    padding: 6px 10px;
    font-size: 0.78rem;
    color: ${(props) => props.theme.$gray800};
    box-shadow: 0 4px 14px rgba(33, 53, 74, 0.12);

    div:first-child {
      font-weight: 700;
    }
  }
`;

// Ref de callback: o gráfico pode sair da tela (poucas amostras) e voltar com outro nó.
function useMeasuredWidth<T extends HTMLElement>() {
  const [width, setWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: T | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!el) return;
    const obs = new ResizeObserver(() => {
      if (el.clientWidth) setWidth(el.clientWidth);
    });
    obs.observe(el);
    observerRef.current = obs;
    setWidth(el.clientWidth);
  }, []);
  return { ref, width };
}

type TooltipState = { x: number; y: number; lines: string[] } | null;

const Tooltip = ({ tip }: { tip: TooltipState }) => {
  if (!tip) return null;
  return (
    <div className="chart-tooltip" style={{ left: tip.x, top: tip.y - 8 }}>
      {tip.lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>
  );
};

export const ChartCard = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) => (
  <CardShell>
    <p className="chart-title">{title}</p>
    {subtitle && <p className="chart-sub">{subtitle}</p>}
    {children}
  </CardShell>
);

export const Histogram = ({ values }: { values: number[] }) => {
  const { ref, width } = useMeasuredWidth<HTMLDivElement>();
  const [tip, setTip] = useState<TooltipState>(null);

  const model = useMemo(() => {
    if (values.length < 3) return null;
    const sorted = values.slice().sort((a, b) => a - b);
    const lo = sorted[Math.floor(0.02 * (sorted.length - 1))];
    const hi = sorted[Math.ceil(0.98 * (sorted.length - 1))];
    if (hi <= lo) return null;
    const nBins = Math.min(14, Math.max(6, Math.round(Math.sqrt(sorted.length))));
    const step = (hi - lo) / nBins;
    const bins: Array<{ x0: number; x1: number; count: number }> = [];
    for (let i = 0; i < nBins; i += 1) {
      bins.push({ x0: lo + i * step, x1: lo + (i + 1) * step, count: 0 });
    }
    let fora = 0;
    values.forEach((v) => {
      if (v < lo || v > hi) {
        fora += 1;
        return;
      }
      const idx = Math.min(nBins - 1, Math.floor((v - lo) / step));
      bins[idx].count += 1;
    });
    const maxCount = bins.reduce((m, b) => Math.max(m, b.count), 1);
    return { bins, lo, hi, fora, maxCount };
  }, [values]);

  const H = 180;
  const AXIS = 22;
  const PAD_T = 8;

  if (!model) {
    return <p className="chart-empty">Amostras insuficientes para o histograma.</p>;
  }

  const plotH = H - AXIS - PAD_T;
  const slot = width > 0 ? width / model.bins.length : 0;
  const barW = Math.min(26, Math.max(4, slot - 3));

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <svg width="100%" height={H} role="img" aria-label="Histograma da distribuição de preços">
        {[0.5, 1].map((f) => (
          <line
            key={f}
            x1={0}
            x2={width}
            y1={PAD_T + plotH - plotH * f}
            y2={PAD_T + plotH - plotH * f}
            stroke={GRID_COLOR}
            strokeWidth={1}
          />
        ))}
        {model.bins.map((b, i) => {
          const h = Math.max(b.count > 0 ? 2 : 0, (b.count / model.maxCount) * plotH);
          const x = i * slot + (slot - barW) / 2;
          const y = PAD_T + plotH - h;
          const r = Math.min(4, barW / 2, h);
          return (
            <path
              key={b.x0}
              d={`M ${x} ${y + r} Q ${x} ${y} ${x + r} ${y} L ${x + barW - r} ${y} Q ${
                x + barW
              } ${y} ${x + barW} ${y + r} L ${x + barW} ${y + h} L ${x} ${y + h} Z`}
              fill={DATA_COLOR}
              onMouseEnter={() =>
                setTip({
                  x: i * slot + slot / 2,
                  y,
                  lines: [
                    `${fmtInt(b.count)} amostra${b.count === 1 ? '' : 's'}`,
                    `${fmtBRLCompact(b.x0)} – ${fmtBRLCompact(b.x1)}`,
                  ],
                })
              }
              onMouseLeave={() => setTip(null)}
            />
          );
        })}
        <line
          x1={0}
          x2={width}
          y1={PAD_T + plotH}
          y2={PAD_T + plotH}
          stroke={GRID_COLOR}
          strokeWidth={1}
        />
        <text x={0} y={H - 4} fontSize={11} fill={TICK_COLOR}>
          {fmtBRLCompact(model.lo)}
        </text>
        <text x={width / 2} y={H - 4} fontSize={11} fill={TICK_COLOR} textAnchor="middle">
          {fmtBRLCompact((model.lo + model.hi) / 2)}
        </text>
        <text x={width} y={H - 4} fontSize={11} fill={TICK_COLOR} textAnchor="end">
          {fmtBRLCompact(model.hi)}
        </text>
      </svg>
      {model.fora > 0 && (
        <p className="chart-note">
          {fmtInt(model.fora)} outlier{model.fora === 1 ? '' : 's'} fora do intervalo p2–p98 omitido
          {model.fora === 1 ? '' : 's'}.
        </p>
      )}
      <Tooltip tip={tip} />
    </div>
  );
};

export const VolumeChart = ({
  points,
  rotulo = { um: 'homologação', varios: 'homologações' },
  barraMax = 26,
}: {
  points: VolumePoint[];
  rotulo?: { um: string; varios: string };
  barraMax?: number;
}) => {
  const { ref, width } = useMeasuredWidth<HTMLDivElement>();
  const [tip, setTip] = useState<TooltipState>(null);
  const [acumulado, setAcumulado] = useState<boolean>(false);

  const serie = useMemo(() => {
    if (!acumulado) return points;
    let soma = 0;
    return points.map((p) => {
      soma += p.n;
      return { month: p.month, n: soma };
    });
  }, [points, acumulado]);

  const H = 180;
  const AXIS = 22;
  const PAD_T = 8;

  if (points.length < 2) {
    return <p className="chart-empty">Poucos meses com amostras para desenhar o volume.</p>;
  }

  const plotH = H - AXIS - PAD_T;
  const maxN = serie.reduce((m, p) => Math.max(m, p.n), 1);
  const slot = width > 0 ? width / serie.length : 0;
  const barW = Math.min(barraMax, Math.max(3, slot - 3));
  const tickIdxs =
    serie.length <= 6
      ? serie.map((_, i) => i)
      : [0, Math.floor((serie.length - 1) / 2), serie.length - 1];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div className="chart-toggle">
        <button
          type="button"
          className={!acumulado ? 'active' : ''}
          onClick={() => setAcumulado(false)}
        >
          Por mês
        </button>
        <button
          type="button"
          className={acumulado ? 'active' : ''}
          onClick={() => setAcumulado(true)}
        >
          Acumulado
        </button>
      </div>
      <svg width="100%" height={H} role="img" aria-label={`${rotulo.varios} por mês`}>
        {[0.5, 1].map((f) => (
          <line
            key={f}
            x1={0}
            x2={width}
            y1={PAD_T + plotH - plotH * f}
            y2={PAD_T + plotH - plotH * f}
            stroke={GRID_COLOR}
            strokeWidth={1}
          />
        ))}
        {serie.map((p, i) => {
          const h = Math.max(p.n > 0 ? 2 : 0, (p.n / maxN) * plotH);
          const x = i * slot + (slot - barW) / 2;
          const y = PAD_T + plotH - h;
          const r = Math.min(4, barW / 2, h);
          return (
            <path
              key={p.month}
              d={`M ${x} ${y + r} Q ${x} ${y} ${x + r} ${y} L ${x + barW - r} ${y} Q ${
                x + barW
              } ${y} ${x + barW} ${y + r} L ${x + barW} ${y + h} L ${x} ${y + h} Z`}
              fill={DATA_COLOR}
              onMouseEnter={() =>
                setTip({
                  x: i * slot + slot / 2,
                  y,
                  lines: [
                    `${fmtInt(p.n)} ${p.n === 1 ? rotulo.um : rotulo.varios}`,
                    `${acumulado ? 'até ' : ''}${fmtMes(p.month)}`,
                  ],
                })
              }
              onMouseLeave={() => setTip(null)}
            />
          );
        })}
        <line
          x1={0}
          x2={width}
          y1={PAD_T + plotH}
          y2={PAD_T + plotH}
          stroke={GRID_COLOR}
          strokeWidth={1}
        />
        {tickIdxs.map((i) => (
          <text
            key={serie[i].month}
            x={i * slot + slot / 2}
            y={H - 4}
            fontSize={11}
            fill={TICK_COLOR}
            textAnchor={i === 0 ? 'start' : i === serie.length - 1 ? 'end' : 'middle'}
          >
            {fmtMes(serie[i].month)}
          </text>
        ))}
      </svg>
      <Tooltip tip={tip} />
    </div>
  );
};

export const TrendChart = ({ points }: { points: TimeseriesPoint[] }) => {
  const { ref, width } = useMeasuredWidth<HTMLDivElement>();
  const [tip, setTip] = useState<TooltipState>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const H = 180;
  const AXIS = 22;
  const PAD_T = 12;
  const PAD_R = 76;

  if (points.length < 2) {
    return <p className="chart-empty">Poucos meses com amostras para desenhar a tendência.</p>;
  }

  const plotH = H - AXIS - PAD_T;
  const plotW = Math.max(0, width - PAD_R);
  const values = points.reduce((acc: number[], p) => acc.concat([p.p25, p.p75, p.mediana]), []);
  const vMin = Math.min.apply(null, values);
  const vMax = Math.max.apply(null, values);
  const span = vMax - vMin || 1;
  const xAt = (i: number) => (points.length === 1 ? 0 : (i / (points.length - 1)) * plotW);
  const yAt = (v: number) => PAD_T + plotH - ((v - vMin) / span) * plotH;

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(p.mediana)}`)
    .join(' ');
  const bandTop = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(p.p75)}`);
  const bandBottom = points
    .slice()
    .reverse()
    .map((p, i) => `L ${xAt(points.length - 1 - i)} ${yAt(p.p25)}`);
  const bandPath = bandTop.concat(bandBottom).concat(['Z']).join(' ');

  const last = points[points.length - 1];
  const tickIdxs =
    points.length <= 4
      ? points.map((_, i) => i)
      : [0, Math.floor((points.length - 1) / 2), points.length - 1];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <svg width="100%" height={H} role="img" aria-label="Evolução mensal da mediana de preços">
        {[0, 0.5, 1].map((f) => (
          <line
            key={f}
            x1={0}
            x2={plotW}
            y1={PAD_T + plotH * f}
            y2={PAD_T + plotH * f}
            stroke={GRID_COLOR}
            strokeWidth={1}
          />
        ))}
        <path d={bandPath} fill={DATA_COLOR} opacity={0.12} />
        <path
          d={linePath}
          fill="none"
          stroke={DATA_COLOR}
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {points.map((p, i) => {
          const active = hoverIdx === i || i === points.length - 1;
          const colW = points.length === 1 ? plotW : plotW / (points.length - 1);
          return (
            <g key={p.month}>
              <rect
                x={i === 0 ? 0 : xAt(i) - colW / 2}
                y={0}
                width={colW}
                height={H}
                fill="transparent"
                onMouseEnter={() => {
                  setHoverIdx(i);
                  setTip({
                    x: xAt(i),
                    y: yAt(p.mediana),
                    lines: [
                      fmtBRL(p.mediana),
                      `${fmtMes(p.month)} · ${fmtInt(p.n)} amostras`,
                      `faixa ${fmtBRLCompact(p.p25)}–${fmtBRLCompact(p.p75)}`,
                    ],
                  });
                }}
                onMouseLeave={() => {
                  setHoverIdx(null);
                  setTip(null);
                }}
              />
              {active && (
                <circle
                  cx={xAt(i)}
                  cy={yAt(p.mediana)}
                  r={4.5}
                  fill={DATA_COLOR}
                  stroke="#fff"
                  strokeWidth={2}
                />
              )}
            </g>
          );
        })}
        <text
          x={xAt(points.length - 1) + 8}
          y={yAt(last.mediana) + 4}
          fontSize={12}
          fontWeight={700}
          fill="#21354A"
        >
          {rotuloCurto(last.mediana)}
        </text>
        {tickIdxs.map((i) => (
          <text
            key={points[i].month}
            x={xAt(i)}
            y={H - 4}
            fontSize={11}
            fill={TICK_COLOR}
            textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
          >
            {fmtMes(points[i].month)}
          </text>
        ))}
      </svg>
      <Tooltip tip={tip} />
    </div>
  );
};

export const RegionChart = ({
  hits,
  valorDe,
  regiaoDestaque,
}: {
  hits: SearchHit[];
  valorDe: (h: SearchHit) => number | null;
  regiaoDestaque: string | null;
}) => {
  const linhas = useMemo(
    () =>
      Object.keys(REGIOES).map((regiao) => {
        const valores = hits
          .filter((h) => REGIOES[regiao].indexOf((h.payload.uf || '').toUpperCase()) >= 0)
          .map(valorDe)
          .filter((v): v is number => typeof v === 'number' && v > 0);
        return { regiao, n: valores.length, mediana: mediana(valores) };
      }),
    [hits, valorDe],
  );

  const nacional = useMemo(
    () => mediana(hits.map(valorDe).filter((v): v is number => typeof v === 'number' && v > 0)),
    [hits, valorDe],
  );

  const maior = linhas.reduce((m, l) => Math.max(m, l.mediana || 0), 0) || 1;
  const totalAmostras = linhas.reduce((soma, l) => soma + l.n, 0);

  return (
    <div>
      {linhas.map((l) => {
        const delta = l.mediana && nacional ? ((l.mediana - nacional) / nacional) * 100 : null;
        const destaque = !regiaoDestaque || l.regiao === regiaoDestaque;
        return (
          <div key={l.regiao} className={`region-row ${destaque ? 'emph' : ''}`}>
            <span className="region-name">{l.regiao}</span>
            <div className="region-track">
              {l.mediana ? (
                <div
                  className="region-bar"
                  style={{ width: `${Math.max(2, (l.mediana / maior) * 100)}%` }}
                  title={`${fmtInt(l.n)} amostras`}
                />
              ) : (
                <span className="chart-empty">sem amostras</span>
              )}
            </div>
            <span className="region-value">
              {l.mediana ? fmtBRLCompact(l.mediana) : '—'}
              {delta !== null && Math.abs(delta) >= 0.5 && (
                <small>
                  {delta > 0 ? '+' : ''}
                  {delta.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%
                </small>
              )}
              <small>
                ({fmtInt(l.n)}
                {totalAmostras > 0 ? ` · ${Math.round((l.n * 100) / totalAmostras)}%` : ''})
              </small>
            </span>
          </div>
        );
      })}
      <p className="chart-note">
        {regiaoDestaque
          ? `Destaque para a região ${regiaoDestaque}. Variação comparada à mediana nacional; entre parênteses, amostras e participação da região.`
          : 'Variação comparada à mediana nacional; entre parênteses, amostras e participação da região.'}
      </p>
    </div>
  );
};
